import * as React from 'react';
import { Input } from './input';
import { Label } from './label';
import { cn } from '@/lib/utils';

export interface MobileInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  containerClassName?: string;
}

/**
 * iOS 風格輸入框（帶標籤）
 * 用於表單輸入，提供 iOS 風格的標籤和輸入框組合
 */
export const MobileInput = React.forwardRef<HTMLInputElement, MobileInputProps>(
  ({ className, label, error, containerClassName, ...props }, ref) => {
    const inputId =
      props.id || `input-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div
        className={cn('space-y-2 w-full', containerClassName)}
        style={{ width: '100%' }}
      >
        {label && (
          <Label
            htmlFor={inputId}
            className="text-sm font-medium text-gray-700"
          >
            {label}
          </Label>
        )}
        <Input
          id={inputId}
          ref={ref}
          className={cn(
            'h-10 w-full rounded-xl border-gray-300 text-left focus:border-primary focus:ring-2 focus:ring-primary/20',
            error &&
              'border-destructive focus:border-destructive focus:ring-destructive/20',
            props.disabled && 'bg-gray-100 cursor-not-allowed',
            // 針對 datetime-local 在手機上的樣式
            props.type === 'datetime-local' && 'mobile-datetime-input',
            className
          )}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            // datetime-local 的 appearance 由 CSS 處理，這裡不設定
            // 以確保與其他輸入欄樣式一致
            ...props.style,
          }}
          {...props}
        />
        {error && <p className="text-sm text-destructive mt-1">{error}</p>}
      </div>
    );
  }
);
MobileInput.displayName = 'MobileInput';

/**
 * iOS 風格文字區域（帶標籤）
 */
export interface MobileTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  containerClassName?: string;
}

export const MobileTextarea = React.forwardRef<
  HTMLTextAreaElement,
  MobileTextareaProps
>(({ className, label, error, containerClassName, ...props }, ref) => {
  const textareaId =
    props.id || `textarea-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={cn('space-y-2 w-full', containerClassName)}>
      {label && (
        <Label
          htmlFor={textareaId}
          className="text-sm font-medium text-gray-700"
        >
          {label}
        </Label>
      )}
      <textarea
        id={textareaId}
        ref={ref}
        className={cn(
          'flex min-h-[80px] w-full rounded-xl border border-gray-300 bg-background px-3 py-2 text-sm text-left ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100 resize-none',
          error &&
            'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/20',
          className
        )}
        {...props}
      />
      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
});
MobileTextarea.displayName = 'MobileTextarea';
